import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { MutationConfig, queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TGetThreadsChannelsResponse, GET_THREADS_CHANNELS_QUERY_KEY } from './get-threads-channels.api';
import { TChannel } from '../../../_shared/types/channel.type';

/**
 * Delete Threads Channel Input Schema
 */
export const deleteThreadsChannelValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteThreadsChannelValidation = z.infer<typeof deleteThreadsChannelValidationSchema>;
export type TDeleteThreadsChannelPayload = TDeleteThreadsChannelValidation;
export type TDeleteThreadsChannelResponse = ApiResponse<TChannel>;

export const deleteThreadsChannel = async (
  payload: TDeleteThreadsChannelPayload
): Promise<TDeleteThreadsChannelResponse> => {
  const url = `/integration/threads/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use Delete Threads Channel React-Query Hook
 */
type UseDeleteThreadsChannelOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteThreadsChannel, AxiosErrorCustom>;
};

export const useDeleteThreadsChannel = ({ mutationConfig }: UseDeleteThreadsChannelOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteThreadsChannel,
    ...restConfig,
  });
};

/**
 * Use Delete Threads Channel Optimistically React-Query Hook
 */
type UseDeleteThreadsChannelOptimisticallyOptions = {
  mutationConfig?: MutationConfig<typeof deleteThreadsChannel>;
};

type TDeleteThreadsChannelOptimisticallyContext = {
  previousData: TGetThreadsChannelsResponse | undefined;
};

export const useDeleteThreadsChannelOptimistically = ({
  mutationConfig,
}: UseDeleteThreadsChannelOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteThreadsChannel,
    onMutate: async (data: TDeleteThreadsChannelValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_THREADS_CHANNELS_QUERY_KEY] });

      const previousData = queryClient.getQueryData<TGetThreadsChannelsResponse>([GET_THREADS_CHANNELS_QUERY_KEY]);

      queryClient.setQueriesData<TGetThreadsChannelsResponse>(
        { queryKey: [GET_THREADS_CHANNELS_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (channel) => channel.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousData } as TDeleteThreadsChannelOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      if ((context as TDeleteThreadsChannelOptimisticallyContext)?.previousData !== undefined) {
        queryClient.setQueryData(
          [GET_THREADS_CHANNELS_QUERY_KEY],
          (context as TDeleteThreadsChannelOptimisticallyContext).previousData
        );
      }
    },

    ...restConfig,
  });
};
